import isNil from 'lodash/isNil';

export const TIPO_COLUMNA_TEXTO = 'texto';
export const TIPO_COLUMNA_NUMERO = 'numero';
export const TIPO_COLUMNA_IMPORTE = 'importe';
export const TIPO_COLUMNA_PORCENTAJE = 'porcentaje';
export const TIPO_COLUMNA_TEMPLATE = 'template';

export class ConfiguracionColumna {
	/**
	 * Tipo de la columna: Texto, Número (entero), Importe, Porcentaje o template.
	 */
	tipo: string;

	/**
	 * Valor a renderizar cuando la celda no tiene valor,
	 *  usualmente seteado desde el método setPlaceholder para que a su vez se setee el renderer genérico.
	 */
	placeholder: string = '';

	//////////////// Atributos relacionados a HandsonTable ////////////////

	/**
	 * Clave de cada item del prop data en CaDatasheet que se debe tomar para renderizar en la tabla.
	 *
	 * Ejemplo, data: [{a: 5, b: 8, c: 10}, {a: 3, b: 2}], Si se quiere renderizar para el primer item el valor 5
	 *  en esta columna, el valor de key debe ser 'a'.
	 */
	key: string = ''; // Clave de cada item del prop data de CaDatasheet que se debe tomar para renderizar en la tabla

	/**
	 * Custom renderer, es la función que define como se renderizará cada celda de la columna.
	 *
	 *  Referencia: https://handsontable.com/docs/7.1.1/Options.html#renderer
	 */
	renderer = (_hotInstance, TD, _row, _col, _prop, value = null, _cellProperties) => {
		let valor_render = value ? value : this.placeholder;
		TD.className = ' ca-datasheet-celda';

		// Validación (solo modifica style, la validación real se realiza en CaDatasheet.vue)
		if (!this.validator(value)) {
			TD.className += ' ca-datasheet-celda--invalid';
			if (this.required && !value) {
				valor_render = 'Requerido'; // Si la validación falló por ser un valor requerido, se indica al usuario.
			}
		}

		// Estilización en modo placeholder (letras grisadas)
		if (!value && this.placeholder)
			TD.className += ' ca-datasheet-celda--placeholder';

		if (!_cellProperties.editor)
			TD.className += ' ca-datasheet-celda--disabled';
			
		if (this.solo_lectura)
			TD.className += ' ca-datasheet-celda--readonly';

		// Modificadores de estilos
		if (this.align)
			TD.className += ' ca-datasheet-celda--' + this.align;

		// Cuando la columna es solo lectura, no hay celda-box, directamente el valor.
		TD.innerHTML = this.solo_lectura ? valor_render : "<div class='celda-box'>" + valor_render + "</div>";
	};

	/**
	 * Validator genérico de acuerdo a los atributos de validación seteados en la configuración de la columna.
	 * Invocado como validator real (https://handsontable.com/docs/7.1.1/Options.html#validator), en método getConfiguracionHT
	 *  y también desde renderer seteado en método setPlaceholder, para setear la clase invalid ya que el renderer la sobreescribe caso contrario.
	 *
	 * @param value
	 */
	validator = (value) => {
		if (!this.required && !value)
			return true;

		if (this.required && !value)
			return false;

		if (!isNil(value) && this.isNumerica() && isNaN(value)) {
			return false;
		}

		if (this.max_length && (value.length > this.max_length))
			return false;

		if (!isNil(this.min_value) && (Number(value) < this.min_value))
			return false;

		if (!isNil(this.max_value) && (Number(value) > this.max_value))
			return false;

		// Paso todas las validaciones
		return true;
	};

	/**
	 * Validator avanzado. Si no se tiene necesidad, utilizar el validator común. Este validator permite evitar el wrapeo
	 * y pasar directamente una función, para poder realizar la validación cruzada entre celdas, pudiendo acceder al scope
	 * de la tabla. De la otra manera, con el validator común, se utiliza una arrow function por lo que el "this" se sobrescribe.
	 * Ejemplo de cómo se utiliza se puede encontrar en TablaConsorcistas.
	 *
	 * Importante: Si se utiliza este validator tener en cuenta que se sobre-escribe el genérico, por lo que los flags
	 * required, max_length, min_value y max_value dejan de tener efecto.
	 */
	validator_avanzado = null;

	//////////////// Atributos de Estilización ////////////////
	solo_lectura = false;
	ancho = 200;
	align: 'left' | 'center' | 'right' = 'center';

	//////////////// Atributos de validación ////////////////
	max_length?: number; // Para columnas de texto
	min_value?: number; // Para columnas numéricas
	max_value?: number; // Para columnas numéricas
	required?: boolean;

	/**
	 * ConfiguracionColumnas constructor
	 *
	 * Seteo de tipo y key, los atributos obligatorios.
	 *
	 * @param tipo
	 * @param key
	 */
	constructor(tipo: string, key: string) {
		this.tipo = tipo;
		this.key = key;
	}

	isNumerica() {
		return [TIPO_COLUMNA_NUMERO, TIPO_COLUMNA_PORCENTAJE, TIPO_COLUMNA_IMPORTE].indexOf(this.tipo) >= 0;
	}

	/**
	 * Vacía la configuración de validación
	 * Útil para casos en donde cambia a lo largo del flujo que se valida y que no.
	 */
	resetValidator() {
		this.max_length = undefined;
		this.min_value = undefined;
		this.max_value = undefined;
		this.required = undefined;
	}

	/**
	 * Retorna formato de configuración esperado por Handsontable (HT)
	 */
	getConfiguracionHT() {
		return {
			type: this.tipo === "texto" ? 'text' : 'numeric',
			numericFormat: {
				pattern: this.tipo === "importe" ? "$ 0.00" : "000.00 %",
				culture: this.tipo === "importe" ? "en-US" : "es-AR"
			},
			// Si es tipo template no se le pasa el renderer ya que se utiliza un componente tipo hot-renderer
			renderer: this.tipo === TIPO_COLUMNA_TEMPLATE ? undefined : this.renderer,
			validator: this.validator_avanzado ? this.validator_avanzado : (value, callback) => {
				callback(this.validator(value))
			},
			readOnly: this.solo_lectura,
			data: this.key
		}
	}

	/**
	 * Retorna valor a copiar desde una celda de la columna, a partir del tipo de columna.
	 *
	 * @param valor_orig - Valor almacenado en la tabla
	 */
	getValorCopiar(valor_orig) {
		switch (this.tipo) {
			case TIPO_COLUMNA_PORCENTAJE:
				let val = parseFloat(valor_orig);
				return !Number.isNaN(val)
					? val.toFixed(5).replace(".", ",")
					: "0";
			case TIPO_COLUMNA_IMPORTE:
			case TIPO_COLUMNA_NUMERO:
			case TIPO_COLUMNA_TEXTO:
			case TIPO_COLUMNA_TEMPLATE:
				return valor_orig;
		}
	}

	/**
	 * Retorna valor a pegar en una celda de la columna, a partir del tipo de columna.
	 *
	 * @param valor_orig - Valor que el usuario tenía copiado
	 */
	getValorPegar(valor_orig) {
		switch (this.tipo) {
			case TIPO_COLUMNA_PORCENTAJE:
				let val = parseFloat(valor_orig);
				return !Number.isNaN(val)
					? valor_orig
					: 0;
			case TIPO_COLUMNA_IMPORTE:
			case TIPO_COLUMNA_NUMERO:
			case TIPO_COLUMNA_TEXTO:
			case TIPO_COLUMNA_TEMPLATE:
				return valor_orig;
		}
	}
}